<template>
  <div
    class="modal fade"
    id="referredCompaniesModal"
    tabindex="-1"
    aria-hidden="true"
    ref="referredCompaniesModal"
  >
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Referred Companies</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body p-0">
          <LoadingIndicatorComponent v-if="loading" />
          <div v-else>
            <!-- Commission Config Display - only visible to admins or the partner themselves -->
            <div
              class="card mb-3 mx-3 mt-3"
              v-if="selectedReferrerData && canViewCommissionConfig"
            >
              <div class="card-header bg-light">
                <h6 class="mb-0">Commission Configuration</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-8">
                    <pre
                      class="mb-0"
                      v-if="selectedReferrerData.commission_config"
                      >{{
                        JSON.stringify(
                          selectedReferrerData.commission_config,
                          null,
                          2
                        )
                      }}</pre>
                    <p v-else class="text-muted mb-0">
                      No commission configuration available
                    </p>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group mb-3">
                      <label for="solderaPriceInput" class="form-label"
                        >Soldera Price (€/MWh)</label
                      >
                      <input
                        type="number"
                        id="solderaPriceInput"
                        class="form-control"
                        v-model="solderaPrice"
                        min="0"
                        step="0.01"
                      />
                      <small class="form-text text-muted">
                        Used to calculate Soldera earnings
                      </small>
                    </div>
                    <div class="form-group">
                      <label for="partnerPriceInput" class="form-label"
                        >Partner Price (€/MWh)</label
                      >
                      <input
                        type="number"
                        id="partnerPriceInput"
                        class="form-control"
                        v-model="partnerPrice"
                        min="0"
                        step="0.01"
                      />
                      <small class="form-text text-muted">
                        Used to calculate Partner earnings
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="table-responsive px-3 pb-3">
              <table class="table table-hover">
                <thead class="table-light">
                  <tr>
                    <th @click="sortReferredCompanies('name')" class="sortable">
                      Company Name
                      <i
                        v-if="referredSortKey === 'name'"
                        :class="referredSortIconClass"
                      ></i>
                    </th>
                    <th
                      @click="sortReferredCompanies('registry_code')"
                      class="sortable"
                    >
                      Registry Code
                      <i
                        v-if="referredSortKey === 'registry_code'"
                        :class="referredSortIconClass"
                      ></i>
                    </th>
                    <th
                      @click="sortReferredCompanies('country')"
                      class="sortable"
                    >
                      Country
                      <i
                        v-if="referredSortKey === 'country'"
                        :class="referredSortIconClass"
                      ></i>
                    </th>
                    <th
                      @click="sortReferredCompanies('signed_at')"
                      class="sortable"
                    >
                      Signed At
                      <i
                        v-if="referredSortKey === 'signed_at'"
                        :class="referredSortIconClass"
                      ></i>
                    </th>
                    <th
                      @click="sortReferredCompanies('total_kw')"
                      class="sortable"
                    >
                      Total kW
                      <i
                        v-if="referredSortKey === 'total_kw'"
                        :class="referredSortIconClass"
                      ></i>
                    </th>
                    <th
                      @click="sortReferredCompanies('soldera_fee')"
                      class="sortable"
                    >
                      Soldera Fee
                      <i
                        v-if="referredSortKey === 'soldera_fee'"
                        :class="referredSortIconClass"
                      ></i>
                    </th>
                    <th
                      @click="sortReferredCompanies('projected_power')"
                      class="sortable"
                    >
                      Projected MWh/Year
                      <i
                        v-if="referredSortKey === 'projected_power'"
                        :class="referredSortIconClass"
                      ></i>
                    </th>
                    <th
                      @click="sortReferredCompanies('soldera_earnings')"
                      class="sortable"
                    >
                      Projected Soldera Earnings
                      <i
                        v-if="referredSortKey === 'soldera_earnings'"
                        :class="referredSortIconClass"
                      ></i>
                    </th>
                    <th
                      v-if="canViewPartnerEarnings"
                      @click="sortReferredCompanies('partner_earnings')"
                      class="sortable"
                    >
                      Projected Partner Earnings
                      <i
                        v-if="referredSortKey === 'partner_earnings'"
                        :class="referredSortIconClass"
                      ></i>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="sortedReferredCompanies.length === 0">
                    <td
                      :colspan="canViewPartnerEarnings ? 9 : 8"
                      class="text-center"
                    >
                      No referred companies found
                    </td>
                  </tr>

                  <template
                    v-for="(monthGroup, index) in companiesByMonth"
                    :key="index"
                  >
                    <!-- Month header row -->
                    <tr class="table-primary">
                      <td colspan="4" class="fw-bold">
                        <i class="bi bi-calendar-month me-2"></i
                        >{{ monthGroup.monthYear }}
                        <span class="ms-2 text-secondary"
                          >({{ monthGroup.companies.length }} companies)</span
                        >
                      </td>
                      <td class="fw-bold text-nowrap">
                        {{ formatLargeNumber(monthGroup.totalKw) }}
                      </td>
                      <td></td>
                      <td class="fw-bold text-nowrap">
                        {{ formatLargeNumber(monthGroup.totalProjectedPower) }}
                      </td>
                      <td class="fw-bold text-nowrap">
                        {{ formatCurrency(monthGroup.totalSolderaEarnings) }}
                      </td>
                      <td
                        v-if="canViewPartnerEarnings"
                        class="fw-bold text-nowrap"
                      >
                        {{ formatCurrency(monthGroup.totalPartnerEarnings) }}
                      </td>
                    </tr>

                    <!-- Company rows for this month -->
                    <tr
                      v-for="company in monthGroup.companies"
                      :key="company.id"
                    >
                      <td>{{ company.name }}</td>
                      <td>{{ company.registry_code }}</td>
                      <td>{{ company.country }}</td>
                      <td>{{ formatDate(company.signed_at) }}</td>
                      <td>{{ formatLargeNumber(company.total_kw || 0) }}</td>
                      <td>{{ formatPercent(company.soldera_fee || 0) }}</td>
                      <td>
                        {{ formatLargeNumber(company.projected_power || 0) }}
                      </td>
                      <td>
                        {{ formatCurrency(calculateSolderaEarnings(company)) }}
                      </td>
                      <td v-if="canViewPartnerEarnings">
                        {{ formatCurrency(calculatePartnerEarnings(company)) }}
                      </td>
                    </tr>
                  </template>
                </tbody>
                <tfoot
                  v-if="sortedReferredCompanies.length > 0"
                  class="table-light fw-bold"
                >
                  <tr>
                    <td colspan="4" class="text-end">Grand Totals:</td>
                    <td>{{ formatLargeNumber(totalReferredKw) }}</td>
                    <td>{{ formatPercent(avgSolderaFee) }}</td>
                    <td>
                      {{ formatLargeNumber(totalReferredProjectedPower) }}
                    </td>
                    <td>{{ formatCurrency(totalSolderaEarnings) }}</td>
                    <td v-if="canViewPartnerEarnings">
                      {{ formatCurrency(totalPartnerEarnings) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatDate, formatLargeNumber } from "@/composables/formatUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { ADMIN_IDS } from "@/constants";
import { formatCurrency } from "@/utils/formatters";
import { useEventListener } from "@vueuse/core";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

// Props
const props = defineProps<{
  companyId: number | null;
  isVisible: boolean;
  accountData: {
    admin_account_id: number | null;
  };
}>();

const emit = defineEmits<{
  close: [];
}>();

// Get global error handler
const { globalErrorHandler } = useGlobalHandlers();

// State
const referredCompanies = ref<any[]>([]);
const loading = ref(false);
const referredSortKey = ref("signed_at");
const referredSortDirection = ref("desc");
const referredCompaniesModal = useTemplateRef<HTMLElement>(
  "referredCompaniesModal"
);
// The dismiss buttons close the dialog through Bootstrap's data-bs-dismiss, which reports it only as
// this DOM event; the parent needs it to drop its visibility flag so the next open reloads.
useEventListener(referredCompaniesModal, "hidden.bs.modal", () =>
  emit("close")
);
const selectedReferrerRate = ref<number>(0.3); // Default commission rate
const selectedReferrerData = ref<any>(null); // Store the selected referrer data
const solderaPrice = ref(1);
const partnerPrice = ref(1);

// Access control computed properties
const isAdmin = computed(() => {
  return (
    props.accountData.admin_account_id &&
    ADMIN_IDS.FULL_ACCESS.includes(props.accountData.admin_account_id)
  );
});

const isPartner = computed(() => {
  // Check if current admin is the partner
  if (!referredCompanies.value.length || !props.accountData.admin_account_id)
    return false;

  // Get the first company to check referrer_account_ids
  const firstCompany = referredCompanies.value[0];
  return (
    firstCompany.referrer_account_ids &&
    firstCompany.referrer_account_ids.includes(
      props.accountData.admin_account_id
    )
  );
});

const canViewCommissionConfig = computed(() => {
  // Admins and partners can view commission config
  return isAdmin.value || isPartner.value;
});

const canViewPartnerEarnings = computed(() => {
  // Admins and partners can view partner earnings
  return isAdmin.value || isPartner.value;
});

// Computed properties
const sortedReferredCompanies = computed(() => {
  return [...referredCompanies.value]
    .map((company) => {
      // Add calculated properties for sorting
      return {
        ...company,
        soldera_earnings: calculateSolderaEarnings(company),
        partner_earnings: calculatePartnerEarnings(company),
      };
    })
    .sort((a, b) => {
      let aValue = a[referredSortKey.value];
      let bValue = b[referredSortKey.value];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";

      // Convert to strings for comparison if they're not numbers
      if (
        typeof aValue !== "number" &&
        typeof bValue !== "number" &&
        !(aValue instanceof Date) &&
        !(bValue instanceof Date)
      ) {
        aValue = String(aValue);
        bValue = String(bValue);
      }

      // Special handling for dates
      if (referredSortKey.value === "signed_at") {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      if (referredSortDirection.value === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
});

// Group companies by month for display
const companiesByMonth = computed(() => {
  // Use the already sorted companies from sortedReferredCompanies
  const companies = [...sortedReferredCompanies.value];

  // Group by month
  const groups: Record<string, any> = {};

  companies.forEach((company) => {
    if (!company.signed_at) return;

    const date = new Date(company.signed_at);
    const monthYear = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
    }).format(date);

    if (!groups[monthYear]) {
      groups[monthYear] = {
        monthYear,
        companies: [],
        totalKw: 0,
        totalProjectedPower: 0,
        totalSolderaEarnings: 0,
        totalPartnerEarnings: 0,
      };
    }

    groups[monthYear].companies.push(company);
    groups[monthYear].totalKw += company.total_kw || 0;
    groups[monthYear].totalProjectedPower += company.projected_power || 0;
    groups[monthYear].totalSolderaEarnings += calculateSolderaEarnings(company);
    groups[monthYear].totalPartnerEarnings += calculatePartnerEarnings(company);
  });

  // Sort the month groups by date (newest first by default)
  return Object.values(groups).sort((a, b) => {
    // Extract month and year from the monthYear string
    const [monthA, yearA] = a.monthYear.split(" ");
    const [monthB, yearB] = b.monthYear.split(" ");

    // Create Date objects for comparison (use 1st day of month)
    const dateA = new Date(`${monthA} 1, ${yearA}`);
    const dateB = new Date(`${monthB} 1, ${yearB}`);

    // Sort by date (newest first)
    return dateB.getTime() - dateA.getTime();
  });
});

const referredSortIconClass = computed(() => {
  return referredSortDirection.value === "asc"
    ? "bi bi-sort-up"
    : "bi bi-sort-down";
});

const totalReferredKw = computed(() => {
  return referredCompanies.value.reduce(
    (sum, company) => sum + (company.total_kw || 0),
    0
  );
});

const totalReferredProjectedPower = computed(() => {
  return referredCompanies.value.reduce(
    (sum, company) => sum + (company.projected_power || 0),
    0
  );
});

const avgSolderaFee = computed(() => {
  if (referredCompanies.value.length === 0) return 0;
  const totalFees = referredCompanies.value.reduce(
    (sum, company) => sum + (company.soldera_fee || 0),
    0
  );
  return totalFees / referredCompanies.value.length;
});

const totalSolderaEarnings = computed(() => {
  return referredCompanies.value.reduce(
    (sum, company) => sum + calculateSolderaEarnings(company),
    0
  );
});

const totalPartnerEarnings = computed(() => {
  return referredCompanies.value.reduce(
    (sum, company) => sum + calculatePartnerEarnings(company),
    0
  );
});

// Methods
const loadReferredCompanies = async () => {
  if (!props.companyId) return;

  loading.value = true;

  try {
    // Fetch referrer data to get commission rate
    const referrerResponse = await goSolidApi.get(
      "/api/admin/commission/dashboard"
    );
    const referrers = referrerResponse.data.data;
    const referrer = referrers.find(
      (item: any) => item.company_id === props.companyId
    );

    if (referrer) {
      selectedReferrerRate.value = referrer.commission_rate;
      selectedReferrerData.value = referrer;

      // Set Soldera price to 1 by default
      solderaPrice.value = 1;

      // Set Partner price based on special_price or default to 1
      if (
        referrer.commission_config &&
        referrer.commission_config.special_price
      ) {
        partnerPrice.value = referrer.commission_config.special_price;
      } else {
        partnerPrice.value = 1;
      }
    } else {
      // If referrer not found, set default values
      selectedReferrerRate.value = 0.3; // Default 30%
      selectedReferrerData.value = null;
      solderaPrice.value = 1;
      partnerPrice.value = 1;
    }

    // Fetch referred companies
    const response = await goSolidApi.get(
      `/api/admin/commission/referred/${props.companyId}`
    );

    // Use the data directly from the server, which now includes projected_power and referrer_account_ids
    referredCompanies.value = response.data.data;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    loading.value = false;
  }
};

const sortReferredCompanies = (key: string) => {
  if (referredSortKey.value === key) {
    referredSortDirection.value =
      referredSortDirection.value === "asc" ? "desc" : "asc";
  } else {
    referredSortKey.value = key;
    referredSortDirection.value = "asc";
  }
};

const formatPercent = (value: number) => {
  return new Intl.NumberFormat("et-EE", {
    style: "percent",
    minimumFractionDigits: 1,
  }).format(value);
};

const calculateSolderaEarnings = (company: any) => {
  const projectedPower = company.projected_power || 0;
  const solderaFee = company.soldera_fee || 0;
  return projectedPower * solderaFee * solderaPrice.value;
};

const calculatePartnerEarnings = (company: any) => {
  const projectedPower = company.projected_power || 0;
  const solderaFee = company.soldera_fee || 0;
  const commissionRate = selectedReferrerRate.value || 0;

  const baseEarnings =
    projectedPower * solderaFee * commissionRate * partnerPrice.value;

  // Check if company has skip_first_year_referral feature flag set
  const hasSkipFlag = company.feature_flags?.skip_first_year_referral === true;

  if (!hasSkipFlag) {
    // No skip flag - show full projected earnings
    return baseEarnings;
  }

  // Company has skip flag - calculate pro-rated earnings based on time passed
  if (company.signed_at) {
    const signedDate = new Date(company.signed_at);
    const now = new Date();
    const monthsSinceSigned =
      (now.getTime() - signedDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44); // 30.44 days average per month

    if (monthsSinceSigned >= 12) {
      // 1+ years have passed - full earnings
      return baseEarnings;
    } else {
      // Pro-rate based on months passed out of 12
      const earningsMultiplier = Math.max(0, monthsSinceSigned / 12);
      return baseEarnings * earningsMultiplier;
    }
  }

  return baseEarnings;
};

// Watch for changes in props
watch(
  () => props.isVisible,
  (newValue) => {
    if (newValue && props.companyId) {
      loadReferredCompanies();
    }
  }
);

// Watch for changes in companyId
watch(
  () => props.companyId,
  (newValue) => {
    if (newValue && props.isVisible) {
      loadReferredCompanies();
    }
  }
);
</script>

<style scoped lang="scss">
.sortable {
  cursor: pointer;
}

.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.table-primary {
  background-color: #e7f1ff;
}

.table-primary td {
  font-size: 1.1rem;
  padding-top: 1rem;
  padding-bottom: 1rem;
  border-top: 1px solid #dee2e6;
}

/* Fix footer positioning */
tfoot tr {
  position: sticky;
  bottom: 0;
  background-color: #f8f9fa;
  z-index: 1;
}

/* Modal specific styles */
.modal-dialog-scrollable .modal-body {
  overflow-y: auto;
}

.modal-xl .table-responsive {
  max-height: 60vh;
  overflow-y: auto;
}
</style>
